/**
 * Edit Content Tool - Schema
 *
 * Supports TWO formats:
 * - V1 (Legacy): cells_to_edit array with col/row addressing (disabled by default)
 * - V2 (Modern): edits array with range-based addressing, auto-fill support
 *
 * The Python backend handles both formats.
 * Use createEditContentParamsSchema(includeV1) to control which formats are available.
 */
import { z } from "zod";
// ═══════════════════════════════════════════════════════════════════════════
// V1 (LEGACY) SCHEMA - cells_to_edit format
// ═══════════════════════════════════════════════════════════════════════════
const NumberFormatV1Schema = z.enum([
    'currency', 'number', 'percentage', 'multiple',
    'year', 'FYyy', 'mm-yyyy', 'mm-yy', 'yyyy', 'ignore'
]);
const CellAddressV1Schema = z.object({
    col: z.number().int().min(1).describe('Column number (1-indexed, A=1)'),
    row: z.number().int().min(1).describe('Row number (1-indexed)'),
    expandToRow: z.number().int().min(1).optional().describe('Fill down to this row (for formulas)'),
    expandToCol: z.number().int().min(1).optional().describe('Fill right to this column (for formulas)'),
});
const CellContentV1Schema = z.object({
    formula: z.string().optional().describe('Formula starting with = (e.g., "=SUM(A1:A5)")'),
    value: z.number().optional().describe('Numeric value to set'),
    text: z.string().optional().describe('Text content to set'),
    clear: z.boolean().optional().describe('Set to true to clear the cell'),
    numberFormat: NumberFormatV1Schema.describe('Number format to apply'),
});
const CellCommentV1Schema = z.object({
    text: z.string().optional().describe('Comment text (keep under 30 chars)'),
    clear: z.boolean().optional().describe('Set to true to clear comment'),
});
const CellEditV1Schema = z.object({
    address: CellAddressV1Schema,
    content: CellContentV1Schema,
    comment: CellCommentV1Schema.optional(),
    spill: z.boolean().optional().describe('Treat as dynamic array formula'),
});
export const EditContentV1ParamsSchema = z.object({
    cells_to_edit: z.array(CellEditV1Schema).describe('Array of cell edits'),
});
// ═══════════════════════════════════════════════════════════════════════════
// V2 (MODERN) SCHEMA - edits array with auto-fill support
// ═══════════════════════════════════════════════════════════════════════════
const RangeSpecSchema = z.object({
    startRow: z.number().int().min(1).describe('Start row (1-indexed)'),
    endRow: z.number().int().min(1).describe('End row (1-indexed)'),
    startCol: z.number().int().min(1).describe('Start column (1-indexed, A=1)'),
    endCol: z.number().int().min(1).describe('End column (1-indexed)'),
});
const EditItemV2Schema = z.object({
    range: RangeSpecSchema.describe('Target range (1-indexed). For fills, specify the full range to fill.'),
    rangeRef: z.string().optional().describe('Excel notation for validation (e.g., "B2:B10")'),
    content: z.union([z.string(), z.number(), z.null()]).describe('Content to fill entire range. Formula references AUTO-ADJUST when filling multi-cell ranges.\n' +
        '- null: Clear all cells in range\n' +
        '- "=formula": Fill with formula. Relative refs adjust (e.g., "=A2*1.1" in B2:B10 becomes B2:=A2*1.1, B3:=A3*1.1, B4:=A4*1.1...)\n' +
        '- string: Fill all cells with text\n' +
        '- number: Fill all cells with numeric value\n' +
        'Use $A$1 for absolute refs that should NOT adjust. Use $A1 to fix column only, A$1 to fix row only.'),
    comment: z.string().optional().describe('Optional comment (under 30 chars)'),
});
export const EditItemSchema = EditItemV2Schema;
const NumberFormatItemV2Schema = z.object({
    range: RangeSpecSchema,
    rangeRef: z.string().optional(),
    format: z.string().describe('Format: currency, number, percentage, or custom Excel format'),
});
/**
 * Copy formatting from a source range to one or more destination ranges.
 * All destination ranges must have the same dimensions as the source.
 */
const CopyFormattingSourceSchema = z.object({
    startRow: z.number().int().min(1).describe('Source start row (1-indexed)'),
    endRow: z.number().int().min(1).describe('Source end row (1-indexed)'),
    startCol: z.number().int().min(1).describe('Source start column (1-indexed, A=1)'),
    endCol: z.number().int().min(1).describe('Source end column (1-indexed)'),
    sheetName: z.string().optional().describe('Source sheet name (defaults to current sheet)'),
});
const CopyFormattingDestSchema = z.object({
    startRow: z.number().int().min(1).describe('Destination start row (1-indexed)'),
    endRow: z.number().int().min(1).describe('Destination end row (1-indexed)'),
    startCol: z.number().int().min(1).describe('Destination start column (1-indexed)'),
    endCol: z.number().int().min(1).describe('Destination end column (1-indexed)'),
    sheetName: z.string().optional().describe('Destination sheet name (defaults to current sheet)'),
});
const CopyFormattingSchema = z.object({
    source: CopyFormattingSourceSchema.describe('Source range to copy formatting from'),
    destinations: z.array(CopyFormattingDestSchema).min(1).describe('Destination ranges (must have same dimensions as source)'),
});
export const EditContentV2ParamsSchema = z.object({
    edits: z.array(EditItemV2Schema).describe('Array of edit operations with auto-fill support'),
    numberFormats: z.array(NumberFormatItemV2Schema).optional().describe('Number formats to apply'),
    copyFormatting: CopyFormattingSchema.optional().describe('Copy formatting from source range to destination ranges (useful for extending tables)'),
});
// ═══════════════════════════════════════════════════════════════════════════
// CONDITIONAL SCHEMA FACTORY
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Create the EditContent params schema with optional V1 support.
 *
 * @param includeV1 - If true, include V1 (cells_to_edit) format. Default: false
 * @returns Zod schema for EditContent params
 */
export function createEditContentParamsSchema(includeV1 = false) {
    if (includeV1) {
        // V1 + V2 unified schema (legacy support)
        return z.object({
            uiDescription: z.string()
                .describe("Brief description (max ~15 words) of what this tool call is attempting."),
            // V1 format
            cells_to_edit: z.array(CellEditV1Schema).optional(),
            // V2 format
            edits: z.array(EditItemV2Schema).optional(),
            numberFormats: z.array(NumberFormatItemV2Schema).optional(),
            // Copy formatting (V2 feature)
            copyFormatting: CopyFormattingSchema.optional().describe('Copy formatting from source range to destination ranges (useful for extending tables)'),
            // Common
            sheetName: z
                .string()
                .optional()
                .describe('Target sheet name (defaults to active sheet). If it does not exist, editContent creates the sheet.'),
        }).refine((data) => data.cells_to_edit !== undefined || data.edits !== undefined, { message: "Either cells_to_edit (V1) or edits (V2) must be provided" });
    }
    // V2-only schema (default, recommended)
    return z.object({
        uiDescription: z.string()
            .describe("Brief description (max ~15 words) of what this tool call is attempting."),
        edits: z.array(EditItemV2Schema).describe('Array of edit operations with auto-fill support'),
        numberFormats: z.array(NumberFormatItemV2Schema).optional().describe('Number formats to apply'),
        copyFormatting: CopyFormattingSchema.optional().describe('Copy formatting from source range to destination ranges (useful for extending tables)'),
        sheetName: z
            .string()
            .optional()
            .describe('Target sheet name (defaults to active sheet). If it does not exist, editContent creates the sheet.'),
    });
}
// ═══════════════════════════════════════════════════════════════════════════
// DEFAULT SCHEMA (V2-only for backwards compatibility with code that imports it)
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Default schema - V2-only (does not include V1 cells_to_edit format)
 * Use createEditContentParamsSchema(true) if you need V1 support.
 */
export const EditContentParamsSchema = createEditContentParamsSchema(false);
export { EditContentParamsSchema as EditContentToolParamsSchema };
